<template>
    <VJoyPanel :id="anchorsPageProfile.experiencesSection" data-testid="profile-experience-section" class="profile-experiences" padding="large">
        <template #panel-title>
            <h2>{{ t('profile.section.experiences.title') }}</h2>
        </template>
        <template #panel-title-action>
            <VJoyWrapper id="profile-experience-section-title-actions" no-margin>
                <LazyExperienceTranslation v-if="isTranslationActivated && experiencesSectionState.length > 0" />
                <LazyExperienceEditionTopActions v-if="isEditionEnabled && (!$ff('profile-revamp') || experiencesSectionState.length > 0)" />
            </VJoyWrapper>
        </template>

        <template #panel-body>
            <template v-if="experiencesSectionState.length > 0">
                <ExperiencesLinkedInHighlight
                    v-if="isEditionEnabled && (!hasRecentExperience || hasOnlyOneExperience)"
                    class="joy-mb-4"
                    :stale="!hasRecentExperience"
                />
                <LazyExperienceListDraggable
                    v-if="isEditionEnabled"
                    :is-enabled-removal="!hasOnlyOneExperience"
                    :is-showing-all-experiences="isShowingAllExperiences"
                />
                <LazyExperiencesList v-else />

                <ShowMoreByItemCount
                    :total="experiencesCount"
                    :limit="EXPERIENCES_TO_DISPLAY_BY_DEFAULT"
                    test-id="toggle-show-all-experiences"
                    :label="t('profile.section.experiences.show.all.label.v2', {count: experiencesCount})"
                    :locked="{
                        allowHref: true,
                        label: t('profile.section.experiences.private', [profile?.firstname]),
                        componentName: 'profile-experiences-section',
                        targetId: anchorsPageProfile.experiencesSection,
                    }"
                    @expand="showExperiences(true)"
                    @collapse="showExperiences(false)"
                />
            </template>
            <template v-else-if="isEditionEnabled && !$ff('profile-revamp')">
                <ul class="profile-experiences__list">
                    <!-- Sample Experience -->
                    <li class="profile-experiences__list-item profile-experiences__sample">
                        <div data-testid="experience-read-only-sample-item" class="profile-experiences__list-item-wrapper">
                            <ExperienceReadOnly :experience="mapToExperienceItem(sampleExperience)"></ExperienceReadOnly>
                        </div>
                    </li>
                </ul>
                <ExperiencesLinkedInHighlight class="joy-mt-4" />
            </template>
            <template v-else-if="isEditionEnabled && $ff('profile-revamp')">
                <EmptyContentSection
                    icon="briefcase"
                    :title="t('profile.section.experiences.empty.content.title')"
                    :description="t('profile.section.experiences.empty.content.description')"
                >
                    <template #action>
                        <ClientOnly>
                            <VJoyButton icon="add" size="small" variant="main" @click="openAddExperience">
                                {{ t('profile.section.experiences.add') }}
                            </VJoyButton>
                            <template #fallback>
                                <VJoyFormFieldSkeleton size="small" width="250px" />
                            </template>
                        </ClientOnly>
                    </template>
                </EmptyContentSection>
                <ExperiencesLinkedInHighlight class="joy-mt-4" />
            </template>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {onUnmounted, watch} from 'vue';
    import {isAfter, subMonths} from 'date-fns';
    import {storeToRefs} from 'pinia';
    import {LazyExperienceEditionTopActions, LazyExperienceListDraggable, LazyExperiencesList, LazyExperienceTranslation} from '#components';
    import {computed, onMounted, ref, useProfilePageViewMode, useTranslation} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {VJoyPanel, VJoyWrapper, VJoyButton, VJoyFormFieldSkeleton} from '@maltjoy/core-vue';
    import type {ExperienceItem} from '~/components/_common/experience/ExperienceItem';
    import ExperienceReadOnly from '~/components/_common/experience/ExperienceReadOnly.vue';
    import ShowMoreByItemCount from '~/components/_common/show-more/ShowMoreByItemCount.vue';
    import EmptyContentSection from '~/components/section/EmptyContentSection.vue';
    import {anchorsPageProfile} from '~/config/anchors';
    import {eventBus} from '~/config/events';
    import ExperiencesLinkedInHighlight from '~/features/experience/ui/ExperiencesLinkedInHighlight.vue';
    import {useProfileStore} from '~/store/profileStore';
    import {useTranslationStore} from '~/store/translation/translationStore';
    import type {AddressResource, LogoResource} from '~/types/api-raw-types';
    import {Experience} from '~/types/domain/Experiences';
    import {experienceMapper} from '~/types/infra/ExperienceMapper';
    import type {ExperienceRequest} from '~/types/infra/ExperienceRequest';
    import {useExperienceSectionState} from '../composable/useExperienceSectionState';

    // Composables
    const {t} = useTranslation();
    const {isEditionEnabled} = useProfilePageViewMode();
    const isNewCompanyLogoEnabled = useFeatureFlag('new-company-logo');

    // State
    const experiencesSectionState = useExperienceSectionState();

    // Stores
    const {reloadExperiences} = useProfileStore();
    const {profile, experiences} = storeToRefs(useProfileStore());
    const {isTranslationActivated} = storeToRefs(useTranslationStore());

    // Refs
    const allExperiences = ref<ExperienceRequest[]>([]);
    const isShowingAllExperiences = ref<boolean>(false);

    // Computed values
    const experiencesCount = computed(() => experiences.value?.items.length || 0);
    const hasOnlyOneExperience = computed(() => experiencesCount.value === 1);
    const hasRecentExperience = computed(() => {
        const sixMonthsAgo = subMonths(new Date(), 6);
        return experiences.value?.items.some((exp) => !exp.endDate || isAfter(new Date(exp.endDate), sixMonthsAgo)) ?? false;
    });

    const EXPERIENCES_TO_DISPLAY_BY_DEFAULT = 3;

    initDraggableList();

    function showExperiences(showAll: boolean): void {
        isShowingAllExperiences.value = showAll;
        initDraggableList();
    }

    const sampleExperience = experienceMapper.toRequest(
        new Experience({
            area: 'SOFTWARE',
            asFreelance: false,
            commentCount: 0,
            company: t('profile.section.experiences.eg.company'),
            companyId: '',
            currentJob: true,
            description: t('profile.section.experiences.eg.description'),
            endDate: '',
            id: '',
            logo: {} as LogoResource,
            organizationName: '',
            recommendationCount: 0,
            recos: [],
            startDate: '1998-09-04',
            location: {formattedAddress: 'Menlo Park, CA, USA'} as AddressResource,
            skills: [
                {
                    id: 'HTML',
                    label: 'HTML',
                    type: 'GLOBAL',
                    origin: 'MANUAL',
                },
                {
                    id: 'CSS',
                    label: 'CSS',
                    type: 'GLOBAL',
                    origin: 'MANUAL',
                },
                {
                    id: 'JS',
                    label: 'JS',
                    type: 'GLOBAL',
                    origin: 'MANUAL',
                },
                {
                    id: 'Photoshop',
                    label: 'Photoshop',
                    type: 'GLOBAL',
                    origin: 'MANUAL',
                },
            ],
            title: t('profile.section.experiences.eg.job'),
        }),
        0,
    );

    function mapToExperienceItem(xpRequest: ExperienceRequest): ExperienceItem {
        return {
            company: xpRequest.company,
            title: xpRequest.title,
            activityArea: xpRequest.area,
            location: xpRequest.location?.formattedAddress,
            startDate: xpRequest.startDate,
            endDate: xpRequest.endDate,
            currentJob: xpRequest.currentJob,
            description: xpRequest.description,
            skills: xpRequest.skills.map((skill) => skill.label),
            logoSrc: xpRequest.logo?.baseUrl
                ? `${xpRequest.logo.baseUrl}/${xpRequest.logo.name}`
                : isNewCompanyLogoEnabled.value
                  ? (xpRequest.logoUrl ?? undefined)
                  : undefined,
            missionId: xpRequest.missionId,
        };
    }

    onMounted(() => {
        eventBus.on('experiences:updated', initDraggableList);
        eventBus.on('resume-import:updated', updateExperienceList);
    });

    onUnmounted(() => {
        eventBus.off('experiences:updated', initDraggableList);
        eventBus.off('resume-import:updated', updateExperienceList);
    });

    watch(
        () => experiences.value?.items,
        () => {
            initDraggableList();
        },
    );

    async function updateExperienceList() {
        await reloadExperiences();
    }

    function initDraggableList() {
        allExperiences.value = experiences.value?.items.map((experience, index) => experienceMapper.toRequest(experience, index)) || [];
        const firstFewExperiences = allExperiences.value.slice(0, EXPERIENCES_TO_DISPLAY_BY_DEFAULT);
        experiencesSectionState.value = isShowingAllExperiences.value ? allExperiences.value : firstFewExperiences;
    }

    function openAddExperience() {
        eventBus.emit('experiences:selection-mode');
    }
</script>

<style lang="scss" scoped>
    .profile-experiences__sample {
        opacity: 0.3;
    }
</style>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .profile-experiences {
        scroll-margin-top: calc(var(--header-menu-height) + var(--joy-core-spacing-2));
    }

    .profile-experiences__list {
        list-style-type: none;
        padding-left: 0;

        &-item {
            &:not(:first-child) {
                padding-top: var(--joy-core-spacing-6);
            }

            &:not(:last-child) {
                padding-bottom: var(--joy-core-spacing-6);
                border-bottom: 1px solid var(--joy-color-neutral-30);
            }

            &-wrapper {
                position: relative;
                display: block;
                width: 100%;
            }

            @media screen and (max-width: 782px) {
                &-wrapper {
                    display: block;
                }
            }
        }
    }
</style>
